/**
 * Utilities for checking authentication token presence in localStorage
 *
 * These are standalone utility functions for checking if auth tokens exist.
 * For Auth0 session checks, use auth0.isAuthenticated()
 *
 * @example
 *  Check if tokens exist before making API call
 * import { hasAuthTokens } from '@/utilities/authTokens';
 * if (!hasAuthTokens()) {
 *   return;
 * }
 *
 * @example
 * Get tokens for manual header injection
 * import { getAuthTokens } from '@/utilities/authTokens';
 * const { accessToken, magicLinkToken } = getAuthTokens();
 */

const MAGIC_LINK_TOKEN_KEY = 'magic_link_token';
const HELD_MAGIC_LINK_TOKEN_KEY = 'magic_link_held_token';

/**
 * Check if access token exists in localStorage
 * @returns {boolean}
 */
export const hasAccessToken = () => {
  return !!localStorage.getItem('access_token');
};

/**
 * Check if magic link token exists in localStorage
 * @returns {boolean}
 */
export const hasMagicLinkToken = () => {
  return !!localStorage.getItem(MAGIC_LINK_TOKEN_KEY);
};

/**
 * Store the magic link token for use by $api
 * @param {string} token
 */
export const saveMagicLinkToken = (token) => {
  localStorage.setItem(MAGIC_LINK_TOKEN_KEY, token);
};

/**
 * Hold a magic link token in sessionStorage while the user completes an Auth0 login, so it's
 * discarded if the tab closes first.
 * @param {string} token
 */
export const holdMagicLinkToken = (token) => {
  sessionStorage.setItem(HELD_MAGIC_LINK_TOKEN_KEY, token);
};

/**
 * Move a held magic link token into localStorage once an Auth0 session exists.
 * @returns {boolean} Whether a held token was attached
 */
export const attachHeldMagicLinkToken = () => {
  const token = sessionStorage.getItem(HELD_MAGIC_LINK_TOKEN_KEY);
  if (!token) {
    return false;
  }
  saveMagicLinkToken(token);
  sessionStorage.removeItem(HELD_MAGIC_LINK_TOKEN_KEY);
  return true;
};

/**
 * Check if both auth tokens exist in localStorage
 * @returns {boolean}
 */
export const hasAuthTokens = () => {
  return hasAccessToken() && hasMagicLinkToken();
};

/**
 * Get both tokens from localStorage
 * @returns {Object} Object containing accessToken and magicLinkToken
 */
export const getAuthTokens = () => {
  return {
    accessToken: localStorage.getItem('access_token'),
    magicLinkToken: localStorage.getItem(MAGIC_LINK_TOKEN_KEY),
  };
};

/**
 * Clear the magic-link tokens and pending-request marker. Magic-link sessions never
 * register with the Auth0 SDK, so this is the only cleanup that actually ends one.
 */
export const clearMagicLinkTokens = () => {
  localStorage.removeItem(MAGIC_LINK_TOKEN_KEY);
  localStorage.removeItem('access_token');
  localStorage.removeItem('magic_link_pending_id');
  sessionStorage.removeItem(HELD_MAGIC_LINK_TOKEN_KEY);
};
